import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Mail, ArrowLeft } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL || "http://localhost:8000";
const API_V2 = `${API}/api/v2`;

export default function ForgotPasswordPage() {
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    const q = searchParams.get("email");
    if (q) {
      try {
        setEmail(decodeURIComponent(q.trim()));
      } catch {
        setEmail(q.trim());
      }
    }
  }, [searchParams]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const trimmed = email.trim();
    if (!trimmed) {
      toast.error("Indiquez votre adresse e-mail.");
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${API_V2}/auth/forgot-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: trimmed }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.detail || "Demande impossible.");
      }
      setSent(true);
      toast.success(data.message || "Demande enregistrée.");
    } catch (err) {
      toast.error(err.message || "Une erreur est survenue.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen w-full bg-zinc-100 px-4 py-10 pt-[calc(120px+env(safe-area-inset-top,0px))] sm:pt-[calc(128px+env(safe-area-inset-top,0px))] md:pt-[calc(128px+env(safe-area-inset-top,0px))] lg:pt-[calc(132px+env(safe-area-inset-top,0px))]">
      <div className="mx-auto w-full max-w-md rounded-2xl border border-zinc-200/90 bg-white p-6 shadow-lg shadow-zinc-200/50 sm:p-8">
        <Link
          to="/connexion"
          className="mb-6 inline-flex items-center gap-2 text-sm font-semibold text-[#190276] hover:underline"
        >
          <ArrowLeft className="h-4 w-4" strokeWidth={2} />
          Retour à la connexion
        </Link>
        <h1 className="font-heading text-2xl font-bold tracking-tight text-zinc-900">
          Mot de passe oublié
        </h1>
        <p className="mt-2 text-[15px] leading-relaxed text-zinc-500">
          Saisissez l&apos;adresse e-mail de votre compte. Si elle est reconnue, vous recevrez un lien pour
          choisir un nouveau mot de passe (valable 1 h).
        </p>

        {sent ? (
          <p className="mt-8 rounded-xl border border-emerald-200 bg-emerald-50/90 px-4 py-3 text-sm text-emerald-900">
            Si un compte existe avec cette adresse, vérifiez votre boîte mail (et le dossier indésirables).
          </p>
        ) : (
          <form onSubmit={handleSubmit} className="mt-8 space-y-5">
            <div>
              <label htmlFor="forgot-email" className="mb-2 block text-sm font-medium text-zinc-700">
                Adresse e-mail <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <Mail className="pointer-events-none absolute left-3.5 top-1/2 h-[18px] w-[18px] -translate-y-1/2 text-zinc-400" />
                <Input
                  id="forgot-email"
                  type="email"
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="h-12 rounded-xl border-zinc-200 bg-zinc-50/80 pl-11 text-[15px] focus-visible:border-[#190276] focus-visible:bg-white focus-visible:ring-2 focus-visible:ring-[#190276]/15"
                  required
                />
              </div>
            </div>
            <Button
              type="submit"
              disabled={loading}
              className="h-12 w-full rounded-xl bg-[#190276] font-semibold hover:bg-[#130160]"
            >
              {loading ? "Envoi…" : "Envoyer le lien"}
            </Button>
          </form>
        )}
      </div>
    </div>
  );
}
